'use client';

import React from 'react';
import { MainSectionLayout } from '@/features/client/MainSectionLayout/MainSectionLayout';
import { MainSection } from '@/features/client/MainSection/MainSection';
import { Container } from '@/components/client/Container/Container';
import { Section } from '@/components/client/Section/Section';
import { ContactUsBanner } from '@/features/client/ContactUsBanner/ContactUsBanner';
import { ServiceCardsSection } from '@/features/client/ServiceCardsSection/ServiceCardsSection';
import { ContentLayout } from '@/features/client/Services/components/ContentLayout/ContentLayout';
import { RequestForm } from '@/features/client/Forms/RequestForm/RequestForm';
import { ServicesNavMenu } from '@/features/client/Services/components/ServicesNavMenu/ServicesNavMenu';
import { Content } from '@/features/client/Services/components/Content/Content';
import image from '@/public/images/services/main_page/electrical.webp';

const data = {
  title: 'Electrical Expertise',
  description: `At Tytum, we're dedicated to innovation and your home comfort. We aim to keep you comfortable year-round with energy-efficient and built to last solutions.\n\nWith extensive experience in the field, we provide a comprehensive range of electrical services to meet all your residential and commercial needs. Our skilled team is equipped to handle projects of any size, ensuring your electrical systems operate safely and efficiently.`,
  image: image,
  offer_title: 'Electrical Services We Offer:',
  offer_data: [
    {
      title: 'Residential Electrical Services:',
      list: [
        { title: 'Electrical Troubleshooting and Repairs' },
        { title: 'New Home Wiring and Rewiring' },
        { title: 'Electrical Panel Upgrades and Replacement' },

        { title: 'Circuit Breaker Installation and Repair' },

        { title: 'Electrical Outlet and Switch Installation' },
        { title: 'GFCI and AFCI Outlet Installation' },
        { title: 'Ceiling Fan Installation and Repair' },
        { title: `Lighting Fixture Installation (Indoor & Outdoor)` },
        { title: 'Surge Protection Installation' },
        { title: 'Smoke and Carbon Monoxide Detector Installation' },
      ],
    },
    {
      title: 'Lighting Solutions: ',
      list: [
        { title: '    LED Lighting Installation and Upgrades' },
        { title: 'Landscape and Security Lighting' },
        { title: 'Recessed Lighting Installation' },
        { title: 'Dimmer Switch Installation' },
        { title: 'Chandelier Installation' },
      ],
    },

    {
      title: 'Electrical Safety Inspections:  ',
      list: [
        { title: 'Comprehensive Electrical Safety Inspections' },
        { title: 'Home Electrical Code Compliance' },
        { title: 'Pre-Purchase Electrical Inspections' },
        { title: 'Electrical Panel Safety Checks' },
      ],
    },

    {
      title: 'Home Automation and Smart Home Solutions:  ',
      list: [
        { title: '    Smart Lighting Systems' },
        { title: 'Smart Thermostat Installation' },
        { title: 'Home Security Systems and Cameras' },
        { title: 'Smart Doorbell Installation' },
        { title: 'Smart Home Integration Services' },
      ],
    },

    {
      title: 'Generator Installation and Maintenance:  ',
      list: [
        { title: '    Whole-House Generator Installation' },
        { title: '    Portable Generator Installation' },
        { title: '    Generator Maintenance and Repair' },
        { title: '    Transfer Switch Installation' },
      ],
    },

    {
      title: 'EV Charger Installation:  ',
      list: [
        { title: 'Electric Vehicle (EV) Charger Installation' },
        { title: 'EV Charger Repair and Maintenance' },
        { title: 'Home and Garage EV Charging Solutions' },
      ],
    },

    {
      title: 'Wiring and Electrical Upgrades:   ',
      list: [
        { title: '    Whole-House Rewiring' },
        {
          title:
            'Wiring for Home Additions and Renovations: seamlessly integrated with full <a href="https://tytum.com/services/remodeling">remodeling services</a> to ensure your upgraded spaces are both functional and stylish.',
        },
        { title: 'Appliance Circuit Installation' },
        { title: 'Dedicated Circuits for Home Offices' },
      ],
    },
    {
      title: 'Electrical Maintenance Plans:  ',
      list: [
        { title: '    Routine Electrical System Check-Ups' },
        { title: 'Annual Electrical Safety Inspections' },
        { title: 'Preventative Maintenance Services' },
      ],
    },
  ],
  banner_title: `“Electrical Services You Can Count On”`,
  banner_description:
    'Power your home with confidence using our expert electrical solutions. From repairs to installations, we ensure safety and reliability in every connection.',
  bookServiceButton: true,
  why_title: 'Why Choose Tytum for Electrical Solutions?',
  why_description:
    'We know how crucial it is to promptly and effectively address problems to ensure the safety and functionality of your home. We deliver quality results on time and within budget, turning your dream home into a reality.',
  why_items: [
    {
      title: 'Licensed Electricians:',
      description:
        'Our expert team is fully licensed, insured, and trained to handle any electrical project safely and efficiently.',
    },
    {
      title: 'Quality Workmanship: ',
      description:
        'We pride ourselves on delivering high-quality electrical services with attention to detail.',
    },
    {
      title: 'Customer-Focused:',
      description:
        'We work closely with our clients to ensure all electrical needs are met with professionalism and care.',
    },
  ],
  why_footerText:
    'Trust us to be your go-to provider in the San Diego area and ensure the comfort and safety of your home. Call us or fill out our online form for questions or a free quote.',
};

const ElectricalPage = () => {
  return (
    <div>
      <MainSectionLayout variant="primary" layout="circle">
        <MainSection
          label="Service Details"
          title="Electrical"
          description="Enjoy perfect indoor temperatures with our expert HVAC services. From installation to maintenance, we make sure your home or business stays cozy all year long. Our technicians also provide <a href='https://tytum.com/services/heating-and-air/air-conditioning'>San Diego air conditioning repair</a>, ensuring your cooling system runs efficiently through the hottest months."
          withContactButton
        />
      </MainSectionLayout>

      <Section variant="secondary">
        <Container>
          <ContentLayout
            contentLeft={
              <>
                <ServicesNavMenu />
                <RequestForm />
              </>
            }
            contentRight={<Content data={data} />}
          />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ServiceCardsSection shortVariant={true} />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ContactUsBanner />
        </Container>
      </Section>
    </div>
  );
};

export default ElectricalPage;
